import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import MarginBadge from '../MarginBadge';
import StockForecast from '../StockForecast';
import { suggestedPrice } from '../../services/margin';

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label} {hint && <span className="text-gray-400">{hint}</span>}</label>
    {children}
  </div>
);

const ProductPricingFields = ({ form, set, setForm, vatRate, defaultMargin, productId }) => {
  const sp = suggestedPrice({ price: parseFloat(form.price) || 0, suppliers: form.suppliers, target_margin_pct: form.target_margin_pct, primary_supplier_id: form.primary_supplier_id }, vatRate, defaultMargin);
  return (
    <>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center justify-between">Prix TTC (€) *
          <MarginBadge compact product={{ price: parseFloat(form.price) || 0, suppliers: form.suppliers, primary_supplier_id: form.primary_supplier_id }} vatRate={vatRate} testId="product-form-margin" />
        </label>
        <Input data-testid="product-price-input" type="number" step="0.01" min="0" value={form.price} onChange={set('price')} placeholder="32.99" />
      </div>
      <Field label="Stock entrepôt (unités)" hint="— 0 = rupture">
        <Input data-testid="product-stock-input" type="number" step="1" min="0" value={form.stock} onChange={set('stock')} placeholder="50" className={String(form.stock) === '0' ? 'border-red-400' : ''} />
        <StockForecast productId={productId} stock={form.stock} />
      </Field>
      <Field label="Seuil d'alerte stock bas" hint="— vide = seuil global (Paramètres)">
        <Input data-testid="product-low-stock-input" type="number" step="1" min="0" value={form.low_stock_threshold} onChange={set('low_stock_threshold')} placeholder="Seuil global" />
      </Field>
      <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-blue-50/40 border border-blue-100 rounded-lg">
        <Field label="Vendu par (multiple)" hint="— 1 = à l'unité">
          <Input data-testid="product-sale-multiple-input" type="number" min="1" max="100" step="1" value={form.sale_multiple} onChange={set('sale_multiple')} />
        </Field>
        <Field label="Libellé du lot" hint="(ex : la paire)">
          <Input data-testid="product-sale-multiple-label-input" value={form.sale_multiple_label} onChange={set('sale_multiple_label')} placeholder="la paire" disabled={(parseInt(form.sale_multiple, 10) || 1) <= 1} />
        </Field>
        <Field label="Poids (kg)" hint="— paliers de livraison">
          <Input data-testid="product-weight-input" type="number" min="0" max="500" step="0.1" value={form.weight_kg} onChange={set('weight_kg')} placeholder="ex. 7.5" />
        </Field>
        <Field label="Marge cible (%)" hint={`— défaut ${defaultMargin} %`}>
          <Input data-testid="product-target-margin-input" type="number" min="0" max="95" step="1" value={form.target_margin_pct} onChange={set('target_margin_pct')} placeholder={String(defaultMargin)} />
        </Field>
        <div className="md:col-span-3 flex flex-wrap items-center gap-3 text-sm" data-testid="product-suggested-price">
          {sp ? (
            <>
              <span className="text-gray-700">Prix conseillé TTC pour {sp.margin} % de marge (PA {sp.purchase.toFixed(2)} € HT) : <strong className="text-[#1e3a5f]" data-testid="product-suggested-price-value">{sp.price.toFixed(2)} €</strong></span>
              {sp.below && <span className="text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">prix actuel en dessous</span>}
              <Button type="button" size="sm" variant="outline" data-testid="product-apply-suggested" onClick={() => setForm(f => ({ ...f, price: sp.price.toFixed(2), original_price: '', discount: '' }))} disabled={Math.abs((parseFloat(form.price) || 0) - sp.price) < 0.005}>Appliquer {sp.price.toFixed(2)} €</Button>
            </>
          ) : <span className="text-gray-500">Renseignez un prix d'achat fournisseur pour obtenir un prix conseillé.</span>}
        </div>
      </div>
      <Field label="Prix barré (€)">
        <Input data-testid="product-original-price-input" type="number" step="0.01" min="0" value={form.original_price} onChange={set('original_price')} placeholder="Optionnel" />
      </Field>
      <Field label="Remise (%)">
        <Input data-testid="product-discount-input" type="number" min="0" max="99" value={form.discount} onChange={set('discount')} placeholder="Auto si prix barré" />
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Note /5">
          <Input type="number" step="0.1" min="0" max="5" value={form.rating} readOnly disabled data-testid="product-form-rating" title="Note par défaut 5/5, recalculée automatiquement à partir des avis clients" />
        </Field>
        <Field label="Avis">
          <Input type="number" min="0" value={form.reviews} readOnly disabled data-testid="product-form-reviews" title="Nombre d'avis clients publiés (automatique)" />
        </Field>
      </div>
      <p className="text-[11px] text-gray-500 mt-1" data-testid="product-form-rating-hint">Note 5/5 par défaut — recalculée automatiquement dès les premiers avis clients.</p>
    </>
  );
};

export default ProductPricingFields;
